import {CommonModule} from '@angular/common';
import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faSquarePlus, faSquareXmark} from '@fortawesome/free-solid-svg-icons';
import {ActivatedRoute, Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {filter, Subscription, take} from 'rxjs';
import {formatOrganizationName, getUpdatedFieldsFormData, validateAllFormFields} from '../../../common/utils';
import {ApiService} from '../../services/api.service';
import {AuthService} from '../../services/auth.service';
import {
    SalesInstallationPartnerSelectComponent,
} from '../../shared/sales-installation-partner-select/sales-installation-partner-select.component';
import {OrganizationDetailContext} from './organization-detail.context';

@Component({
    selector: 'app-organization-profile',
    standalone: true,
    imports: [
        CommonModule,
        FontAwesomeModule,
        ReactiveFormsModule,
        FormsModule,
        SalesInstallationPartnerSelectComponent,
    ],
    templateUrl: './organization-profile.component.html',
    styleUrl: './organization-profile.component.css',
})
export class OrganizationProfileComponent implements OnInit, OnDestroy {
    canEditOrganizationName = false;
    editOrganizationForm!: FormGroup;
    organizationDetail: any = {};
    formChanged: Boolean = false;
    organizationId: string | null = null;
    loadError: string | null = null;

    addressTypes = [
        'Hotel',
        'Airport',
        'Home',
        'Appartment',
        'Office',
        'Mall',
        'Other',
        'Fleet Yard',
    ];
    customerSources = [
        ['DIRECT', 'Direct'],
        ['INDIA_MART', 'IndiaMART'],
        ['WEBSITE', 'Website'],
        ['REFERENCE', 'Reference'],
        ['SALES_INSTALLATION_PARTNER', 'Sales & Installation Partner'],
        ['OTHER', 'Other'],
    ];
    installationOptions = [
        ['KYRA_TEAM', 'Kyra Team'],
        ['SALES_INSTALLATION_PARTNER', 'Sales & Installation Partner'],
        ['CUSTOMER_TEAM', 'Customer Team'],
        ['OTHER_VENDOR', 'Other Vendor'],
    ];
    fieldMapping: any = {
        name: 'name',
        pincode: 'address.pincode',
        city: 'address.city',
        state: 'address.state',
        locality: 'address.locality',
        flatBuildingName: 'address.flat_building_name',
        landmark: 'address.landmark',
        addressType: 'address.address_type',
        isDefault: 'address.is_default',
        customer_source: 'customer_source',
        source_sales_installation_partner: 'source_sales_installation_partner',
        installation_by: 'installation_by',
        installation_sales_installation_partner: 'installation_sales_installation_partner',
    };

    private routeSub: Subscription | null = null;
    private contextSub: Subscription | null = null;
    private loadErrorSub: Subscription | null = null;
    private formChangeSub: Subscription | null = null;
    private partnerSubs = new Subscription();
    private formBound = false;

    constructor(
        private apiService: ApiService,
        library: FaIconLibrary,
        private router: Router,
        private toastr: ToastrService,
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private authService: AuthService,
        private organizationContext: OrganizationDetailContext,
    ) {
        library.addIcons(faSquarePlus, faSquareXmark);
        this.editOrganizationForm = this.fb.group({
            name: ['', Validators.required],
            pincode: ['', Validators.required],
            city: ['', Validators.required],
            state: ['', Validators.required],
            locality: ['', Validators.required],
            flatBuildingName: ['', Validators.required],
            landmark: [''],
            addressType: ['', Validators.required],
            isDefault: ['', Validators.required],
            timezone: ['', Validators.required],
            customer_source: [''],
            source_sales_installation_partner: [null],
            installation_by: [''],
            installation_sales_installation_partner: [null],
        });
    }

    ngOnInit(): void {
        this.canEditOrganizationName = this.authService.isSuperUser();
        this.loadErrorSub = this.organizationContext.loadError$.subscribe((error) => {
            this.loadError = error;
        });
        this.routeSub = this.route.parent!.paramMap.subscribe((params) => {
            const nextId = params.get('organizationId');
            if (nextId && nextId !== this.organizationId) {
                this.organizationId = nextId;
                this.formBound = false;
                this.bindOrganization(nextId);
            }
        });
    }

    ngOnDestroy(): void {
        this.persistDraftIfNeeded();
        this.routeSub?.unsubscribe();
        this.contextSub?.unsubscribe();
        this.loadErrorSub?.unsubscribe();
        this.formChangeSub?.unsubscribe();
        this.partnerSubs.unsubscribe();
    }

    get showSourcePartnerField(): boolean {
        return this.editOrganizationForm.get('customer_source')?.value === 'SALES_INSTALLATION_PARTNER';
    }

    get showInstallationPartnerField(): boolean {
        return this.editOrganizationForm.get('installation_by')?.value === 'SALES_INSTALLATION_PARTNER';
    }

    private bindOrganization(pk: string): void {
        this.contextSub?.unsubscribe();
        const draft = this.organizationContext.getProfileDraft(pk);
        if (draft) {
            this.applyOrganization(draft.organizationDetail, draft.formValue, true);
            return;
        }

        this.contextSub = this.organizationContext.organization$.pipe(
            filter((org) => !!org && String(org.id) === String(pk)),
            take(1),
        ).subscribe((response) => {
            // A draft may have been saved while waiting (unlikely); never clobber dirty state.
            const pendingDraft = this.organizationContext.getProfileDraft(pk);
            if (pendingDraft) {
                this.applyOrganization(pendingDraft.organizationDetail, pendingDraft.formValue, true);
                return;
            }
            if (this.formBound && this.formChanged) {
                return;
            }
            this.applyOrganization(response);
        });
    }

    private applyOrganization(
        response: any,
        formValueOverride?: Record<string, unknown>,
        restoreDraft = false,
    ): void {
        this.organizationDetail = response;
        this.partnerSubs.unsubscribe();
        this.partnerSubs = new Subscription();

        const values = formValueOverride || {
            name: this.organizationDetail.name,
            pincode: this.organizationDetail.address.pincode,
            city: this.organizationDetail.address.city,
            state: this.organizationDetail.address.state,
            locality: this.organizationDetail.address.locality,
            flatBuildingName: this.organizationDetail.address.flat_building_name,
            landmark: this.organizationDetail.address.landmark,
            addressType: this.organizationDetail.address.address_type,
            isDefault: this.organizationDetail.address.is_default,
            timezone: this.organizationDetail.timezone,
            customer_source: this.organizationDetail.customer_source || '',
            source_sales_installation_partner:
                this.organizationDetail.source_sales_installation_partner || null,
            installation_by: this.organizationDetail.installation_by || '',
            installation_sales_installation_partner:
                this.organizationDetail.installation_sales_installation_partner || null,
        };

        this.editOrganizationForm = this.fb.group({
            name: [values['name'], Validators.required],
            pincode: [values['pincode'], Validators.required],
            city: [values['city'], Validators.required],
            state: [values['state'], Validators.required],
            locality: [values['locality'], Validators.required],
            flatBuildingName: [values['flatBuildingName'], Validators.required],
            landmark: [values['landmark']],
            addressType: [values['addressType'], Validators.required],
            isDefault: [values['isDefault'], Validators.required],
            timezone: [values['timezone'], Validators.required],
            customer_source: [values['customer_source']],
            source_sales_installation_partner: [values['source_sales_installation_partner']],
            installation_by: [values['installation_by']],
            installation_sales_installation_partner: [values['installation_sales_installation_partner']],
        });
        this.setupPartnerFieldHandlers();
        this.formChangeSub?.unsubscribe();
        this.formChangeSub = this.editOrganizationForm.valueChanges.subscribe(() => {
            this.checkFormChanges();
        });
        this.formBound = true;
        if (restoreDraft) {
            this.checkFormChanges();
        } else {
            this.formChanged = false;
        }
    }

    private persistDraftIfNeeded(): void {
        if (!this.organizationId || !this.formBound) {
            return;
        }
        if (!this.formChanged) {
            // Drop any obsolete draft once the form is clean again.
            this.organizationContext.clearProfileDraft(this.organizationId);
            return;
        }
        if (!this.organizationDetail?.id) {
            return;
        }
        this.organizationContext.saveProfileDraft({
            organizationId: this.organizationId,
            formValue: {...this.editOrganizationForm.getRawValue()},
            organizationDetail: this.organizationDetail,
        });
    }

    onPincodeChange(): void {
        const pincode = this.editOrganizationForm.get('pincode')?.value;
        if (pincode && pincode.length === 6) {
            this.fetchCityState(pincode);
        }
    }

    fetchCityState(pincode: number): void {
        this.apiService.getCityState(pincode).subscribe({
            next: (data: any) => {
                if (data.length > 0) {
                    data = data[0];
                    this.editOrganizationForm.patchValue({
                        city: data.district_name,
                        state: data.state_name,
                    });
                    this.editOrganizationForm.get('city')?.markAsDirty();
                    this.editOrganizationForm.get('state')?.markAsDirty();
                }
            },
            error: (err) => {
                if (err['error']?.['detail']) {
                    this.toastr.error(err['error']['detail'], 'Error');
                }
            },
        });
    }

    onSubmit(): void {
        if (this.organizationDetail.id && this.editOrganizationForm.valid) {
            const updatedFields = getUpdatedFieldsFormData(this.editOrganizationForm, this.fieldMapping);
            if (this.editOrganizationForm.get('name')?.dirty) {
                updatedFields.set('name', formatOrganizationName(this.editOrganizationForm.value.name));
            }
            this.apiService.editOrganization(this.organizationDetail.id, updatedFields).subscribe({
                next: () => {
                    this.toastr.success('Customer Updated Successfully', 'Success');
                    if (this.organizationId) {
                        this.organizationContext.clearProfileDraft(this.organizationId);
                    }
                    this.formChanged = false;
                    this.redirect();
                },
                error: (err) => {
                    if (err['error']?.['non_field_errors']) {
                        err['error']['non_field_errors'].forEach((element: any) => {
                            this.toastr.error(element, 'Error');
                        });
                    } else if (err['error']?.['name']) {
                        err['error']['name'].forEach((element: any) => {
                            this.toastr.error(element, 'Error');
                        });
                    } else if (err['status'] !== 403 && err.status !== 0) {
                        this.toastr.error('Something went wrong!', 'Error');
                    }
                },
            });
        } else {
            validateAllFormFields(this.editOrganizationForm);
        }
    }

    redirect(): void {
        const returnTab = this.route.snapshot.queryParamMap.get('returnTab') === 'organization'
            ? 'organization'
            : 'customer';
        this.router.navigate(['organization'], {state: {activeTab: returnTab}});
    }

    onCancel(): void {
        if (this.organizationId) {
            this.organizationContext.clearProfileDraft(this.organizationId);
        }
        this.formChanged = false;
        this.redirect();
    }

    checkFormChanges(): void {
        if (!this.organizationDetail) {
            return;
        }
        const formValue = this.editOrganizationForm.value;
        this.formChanged =
            formValue.name !== this.organizationDetail.name
            || formValue.pincode !== this.organizationDetail.address.pincode
            || formValue.city !== this.organizationDetail.address.city
            || formValue.state !== this.organizationDetail.address.state
            || formValue.locality !== this.organizationDetail.address.locality
            || formValue.flatBuildingName !== this.organizationDetail.address.flat_building_name
            || formValue.landmark !== this.organizationDetail.address.landmark
            || formValue.addressType !== this.organizationDetail.address.address_type
            || formValue.isDefault !== this.organizationDetail.address.is_default
            || formValue.timezone !== this.organizationDetail.timezone
            || formValue.customer_source !== (this.organizationDetail.customer_source || '')
            || formValue.source_sales_installation_partner
                !== (this.organizationDetail.source_sales_installation_partner || null)
            || formValue.installation_by !== (this.organizationDetail.installation_by || '')
            || formValue.installation_sales_installation_partner
                !== (this.organizationDetail.installation_sales_installation_partner || null);
        if (!this.formChanged && this.organizationId) {
            this.organizationContext.clearProfileDraft(this.organizationId);
        }
    }

    setSourcePartner(partnerId: number | null): void {
        const control = this.editOrganizationForm.get('source_sales_installation_partner');
        control?.setValue(partnerId);
        control?.markAsDirty();
        control?.markAsTouched();
    }

    setInstallationPartner(partnerId: number | null): void {
        const control = this.editOrganizationForm.get('installation_sales_installation_partner');
        control?.setValue(partnerId);
        control?.markAsDirty();
        control?.markAsTouched();
    }

    private setupPartnerFieldHandlers(): void {
        this.partnerSubs.add(
            this.editOrganizationForm.get('customer_source')!.valueChanges.subscribe((value) => {
                if (
                    value !== 'SALES_INSTALLATION_PARTNER'
                    && this.editOrganizationForm.get('source_sales_installation_partner')?.value
                ) {
                    this.editOrganizationForm.patchValue(
                        {source_sales_installation_partner: null},
                        {emitEvent: false},
                    );
                }
            }),
        );
        this.partnerSubs.add(
            this.editOrganizationForm.get('installation_by')!.valueChanges.subscribe((value) => {
                if (
                    value !== 'SALES_INSTALLATION_PARTNER'
                    && this.editOrganizationForm.get('installation_sales_installation_partner')?.value
                ) {
                    this.editOrganizationForm.patchValue(
                        {installation_sales_installation_partner: null},
                        {emitEvent: false},
                    );
                }
            }),
        );
    }
}
